import { CampaignCreatorStore } from "../types/ReduxTypes/StoreTypes/CampaignCreatorStore";
import { CookieSet, GeoTargetOptionsData, RoleTitleGroup } from "../types/campaignTypes";
import { filterRealGeoStates, isGeoPlaceholderState } from "./geoStateFilters";
import { walkRoleSets } from "../views/redesign/RolesTitlesView/roleTitleSetUtils";

export type TargetingSummaryRow = {
  label: string;
  value: string;
};

const LIST_PREVIEW_LIMIT = 12;

export const formatTargetingList = (items: string[]): string => {
  if (!items.length) return "—";
  if (items.length <= LIST_PREVIEW_LIMIT) return items.join(", ");
  return `${items.slice(0, LIST_PREVIEW_LIMIT).join(", ")} (+${items.length - LIST_PREVIEW_LIMIT} more)`;
};

const resolveGeoStateNames = (selectedIds: string[], geoTargetOptions: GeoTargetOptionsData | null): string[] => {
  if (!selectedIds.length || !geoTargetOptions) return selectedIds;
  const states = filterRealGeoStates([
    ...(geoTargetOptions["United States"]?.states ?? []),
    ...(geoTargetOptions.Canada?.states ?? []),
  ]);
  const byId = new Map(states.map((s) => [String(s.id), s.name]));
  return selectedIds
    .map((id) => byId.get(id))
    .filter((name): name is string => Boolean(name) && !isGeoPlaceholderState({ name: name ?? "" }))
    .sort((a, b) => a.localeCompare(b));
};

const resolveRoleTitleNames = (
  campaignCookieSets: Record<string, CookieSet[]> | null,
  roleTitleList: RoleTitleGroup[],
): string[] => {
  if (!campaignCookieSets) return [];

  const names: string[] = [];
  for (const group of roleTitleList) {
    walkRoleSets(group.roleSet, (roleSet) => {
      const key = `${group.roleTypeId}:${roleSet.roleId}`;
      if (campaignCookieSets[key]?.length) {
        names.push(`${group.roleType} / ${roleSet.role}`);
      }
    });
  }

  if (names.length) return names;

  const titles = new Set<string>();
  Object.entries(campaignCookieSets).forEach(([key, cookies]) => {
    if (key.includes("_ALL") || !cookies?.length) return;
    cookies.forEach((c) => {
      if (c.title) titles.add(c.title);
    });
  });
  return [...titles];
};

export const buildTargetingSummaryRows = (campaignToCreate: CampaignCreatorStore): TargetingSummaryRow[] => {
  const {
    selectedDomainListsIds,
    selectedDomainNames,
    publicDomainLists,
    customDomainLists,
    selectedGeoTargetOptions,
    geoTargetOptions,
    selectedZipCodes,
    campaignCookieSets,
    roleTitleList,
    foundCompanyInfo,
    selectedSegmentIds,
    foundEmailsInfo,
  } = campaignToCreate;

  const allPublicationLists = [...(publicDomainLists ?? []), ...(customDomainLists ?? [])];
  const publicationListNames = (selectedDomainListsIds ?? [])
    .map((id) => allPublicationLists.find((l) => l.id === id)?.name)
    .filter((name): name is string => Boolean(name));

  const includedCompanies = [...(foundCompanyInfo?.primarySegments ?? []), ...(foundCompanyInfo?.uniqueSegments ?? [])]
    .filter((c) => c.include && c.domainInfo)
    .map((c) => c.domain);

  const crmEmailCount = (foundEmailsInfo?.emailsToHash ?? []).reduce(
    (sum, item) => sum + (item.emails?.length ?? 0),
    0,
  );
  const crmDomains = (foundEmailsInfo?.emailsToHash ?? [])
    .map((item) => item.domain)
    .filter((d) => d && d !== "general");

  const roleTitles = resolveRoleTitleNames(campaignCookieSets, roleTitleList ?? []);
  const geoStates = resolveGeoStateNames(selectedGeoTargetOptions ?? [], geoTargetOptions);

  const rows: TargetingSummaryRow[] = [];

  if (publicationListNames.length || (selectedDomainNames?.length ?? 0) > 0) {
    rows.push({
      label: "Publication Lists",
      value: publicationListNames.length
        ? formatTargetingList(publicationListNames)
        : `${selectedDomainNames.length} domain${selectedDomainNames.length === 1 ? "" : "s"} selected`,
    });
  }

  if (geoStates.length) {
    rows.push({ label: "Geo States / Provinces", value: formatTargetingList(geoStates) });
  }

  if (selectedZipCodes?.length) {
    rows.push({ label: "Zip Codes", value: formatTargetingList(selectedZipCodes) });
  }

  if (roleTitles.length) {
    rows.push({ label: "Roles & Titles", value: formatTargetingList(roleTitles) });
  }

  if (includedCompanies.length) {
    rows.push({ label: "Target Companies", value: formatTargetingList(includedCompanies) });
  }

  if (selectedSegmentIds?.length) {
    rows.push({
      label: "Visitors Retargeting Segments",
      value: `${selectedSegmentIds.length} segment${selectedSegmentIds.length === 1 ? "" : "s"} selected`,
    });
  }

  if (crmEmailCount > 0) {
    rows.push({
      label: "CRM Retargeting",
      value: crmDomains.length
        ? `${crmEmailCount} email${crmEmailCount === 1 ? "" : "s"} across ${formatTargetingList(crmDomains)}`
        : `${crmEmailCount} email${crmEmailCount === 1 ? "" : "s"} selected`,
    });
  }

  return rows;
};
